import classNames from 'classnames';
import { useContext } from 'react';
import { PrimaryButton } from '@/common/Button';
import { FinanceStatus } from '~/components/common/FinanceCard/constants';
import StatusIndicator from '~/components/common/StatusIndicator';
import SoftPullStatus from '~/constants/softPullStatus';
import ModalContext from '~/context/vdp/ModalContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import { useGetShowEVTaxTreatment } from '~/hooks/vdp';
import { useButtonHandler } from '~/hooks/vdp/cta';
import useSoftPullStatus from '~/hooks/vdp/selectors/useSoftPullStatus';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import {
  ButtonWrapper,
  CardHeader,
  Contents,
  LineItem,
  Price,
  PriceBreakDown,
  PriceSection,
  SummaryLineItem,
  Title
} from './Card.styles';
import CommonCard from './CommonCard';
import usePaymentSummary from '../../hooks/selectors/useGetPaymentSummary';
import useGetStartedText from '../../hooks/selectors/useGetStartedText';
import CkPromoLineItem from '../CkPromoLineItem';
import { CleanTaxSavingsCard } from '../CleanTaxSavingsCard';
import TradeInSection from '../TradeInSection';

const nbsp = '\u00A0';

type OwnProps = {
  isSingleColumn?: boolean;
};

export default function PayOnceCard({ isSingleColumn }: OwnProps) {
  const { setSoftPullModalOpen } = useContext(ModalContext);
  const { softPullStatus } = useSoftPullStatus();
  const { vdpPricingInfo } = useContext(UserVehicleContext);
  const hasExpiredFinancing = softPullStatus === SoftPullStatus.Expired;
  const {
    credits: { creditKarmaPromoAmount, tradeInCredit, totalDiscountAmount },
    debits: { deliveryCharge, taxTitleReg, vehiclePrice },
    totals: { totalPrice, subTotal }
  } = usePaymentSummary();
  const getStarted = useButtonHandler();

  const getStartedText = useGetStartedText(hasExpiredFinancing);
  const shouldShowEvTreatment = useGetShowEVTaxTreatment();

  const showSubtotal = Boolean(tradeInCredit);
  const discountLineItem = creditKarmaPromoAmount
    ? totalDiscountAmount - creditKarmaPromoAmount
    : totalDiscountAmount;

  const handleCTAClick = () => {
    setSoftPullModalOpen(true);
  };

  return (
    <CommonCard
      isLoading={vdpPricingInfo.isLoading}
      isLongCard={hasExpiredFinancing}
    >
      {hasExpiredFinancing ? (
        <CardHeader>
          <div className="flex items-center justify-between">
            <p className="tl-header-m">Your Financing</p>
            <StatusIndicator status={FinanceStatus.Expired} text="Expired" />
          </div>
          <p className="text-grey-5 tl-body-l">
            Refresh to get instantly pre-qualified. No hit to your credit.
          </p>
          <PrimaryButton
            className="mx-auto"
            onClick={handleCTAClick}
            data-analytics-id={null}
            size="medium"
          >
            Refresh Your Terms
          </PrimaryButton>
        </CardHeader>
      ) : (
        <Title>Pay Once</Title>
      )}

      <Contents>
        <PriceSection>
          <Price>${formatAndDropDecimal(totalPrice)}</Price>
          <p className="text-grey-5 tl-body-m">
            {deliveryCharge ? nbsp : 'Free Shipping'}
          </p>
          {shouldShowEvTreatment && (
            <CleanTaxSavingsCard
              className={classNames({
                'mt-16': !deliveryCharge
              })}
            />
          )}
        </PriceSection>

        <PriceBreakDown>
          <LineItem>
            <span>Vehicle price</span>
            <span>${formatAndDropDecimal(vehiclePrice)}</span>
          </LineItem>

          <LineItem className={creditKarmaPromoAmount > 0 ? '' : 'hidden'}>
            <CkPromoLineItem />
            <span>- ${formatAndDropDecimal(creditKarmaPromoAmount)}</span>
          </LineItem>

          <LineItem className={discountLineItem > 0 ? '' : 'hidden'}>
            <span>Discount</span>
            <span>- ${formatAndDropDecimal(discountLineItem)}</span>
          </LineItem>

          <LineItem data-qa="shipping-fee-line">
            <span>{deliveryCharge ? 'Nonrefundable s' : 'S'}hipping fee</span>
            <span>${formatAndDropDecimal(deliveryCharge)}</span>
          </LineItem>

          <LineItem className={classNames(showSubtotal ? 'mb-16' : 'mb-0')}>
            <span>Tax, title, registration</span>
            <span>${formatAndDropDecimal(taxTitleReg)}</span>
          </LineItem>

          <ul
            className={classNames('border-t border-grey-2 pt-16', {
              hidden: !showSubtotal
            })}
            data-qa="trade-in-details"
          >
            <SummaryLineItem>
              <span>Subtotal</span>
              <span>${formatAndDropDecimal(subTotal)}</span>
            </SummaryLineItem>
            <TradeInSection
              tradeInCredit={tradeInCredit}
              hasDeliveryCharge={Boolean(deliveryCharge)}
              card="once"
            />
          </ul>
        </PriceBreakDown>

        <ul>
          <div
            className={classNames(
              'rounded-[8px] border border-solid border-grey-3 p-16'
            )}
          >
            <SummaryLineItem className="mb-0">
              <span>Total Purchase Price</span>
              <span>${formatAndDropDecimal(totalPrice)}</span>
            </SummaryLineItem>
            <li
              className={classNames('mt-8 text-grey-5 tl-body-xs m:tl-body-s', {
                hidden: !deliveryCharge
              })}
            >
              Includes ${formatAndDropDecimal(deliveryCharge)} nonrefundable
              shipping fee
            </li>
          </div>
        </ul>

        <ButtonWrapper className={getStartedText ? 'mt-24' : 'hidden'}>
          <PrimaryButton
            className={isSingleColumn ? 'mx-auto' : 'w-full'}
            onClick={getStarted}
            data-analytics-id="Price Details - Pay Once - Get Started"
            size="medium"
          >
            {getStartedText}
          </PrimaryButton>
        </ButtonWrapper>
      </Contents>
    </CommonCard>
  );
}
